"use client";

import { c as _c } from "react/compiler-runtime";
import { CircleAlert, CircleCheck, ExternalLink, Hourglass, OctagonAlert } from "lucide-react";
import { explorerTxBase } from "@/features/collections/detail/parts";
import type { AuctionItem } from "../../types";
import type { useAuctionPage } from "./useAuctionPage";
type Page = ReturnType<typeof useAuctionPage>;

/**
 * The settlement, said on the decision card — in the words of whoever is
 * reading: the owner (who is paid), the winner (who receives the work), or
 * anyone else (who only needs to know bidding is closed).
 *
 *  settling — the transfer and the payment are running on chain; it is told
 *             here when they finish, the page need not stay open;
 *  settled  — done, with the two transactions on the chain's explorer;
 *  failed   — the backend's reason; the auction is active again, so the owner
 *             may choose the same bid again;
 *  stuck    — the backend's reason; the payment is stuck — the owner must NOT
 *             choose again (the button is gone until the auction moves).
 */
export default function SettlementNotice(t0) {
  const $ = _c(65);
  const {
    auction,
    p,
    lang
  } = t0;
  let t1;
  if ($[0] !== lang) {
    t1 = (fa, en) => lang === "fa" ? fa : en;
    $[0] = lang;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  const s = p.settlement;
  if (s.state === "none") {
    return null;
  }
  const isWinner = Boolean(p.winnerBid && Number(p.winnerBid.user_id ?? p.winnerBid.user?.id ?? 0) === p.viewerId);
  const who = p.isOwner ? "owner" : isWinner ? "winner" : "other";
  const t2 = auction.network?.name;
  let t3;
  if ($[2] !== t2) {
    t3 = explorerTxBase(t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const explorer = t3;
  if (s.state === "settling") {
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Hourglass aria-hidden={true} className="ap-settle-ico" />;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] !== t) {
      t5 = t("\u062F\u0631 \u062D\u0627\u0644 \u062A\u0633\u0648\u06CC\u0647", "Settling");
      $[5] = t;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    let t6;
    if ($[7] !== t5) {
      t6 = <b>{t5}</b>;
      $[7] = t5;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== t || $[10] !== who) {
      t7 = who === "owner" ? t("\u0628\u0631\u0646\u062F\u0647 \u0627\u0646\u062A\u062E\u0627\u0628 \u0634\u062F. \u0627\u0646\u062A\u0642\u0627\u0644 \u0627\u062B\u0631 \u0628\u0647 \u0628\u0631\u0646\u062F\u0647 \u0648 \u067E\u0631\u062F\u0627\u062E\u062A \u0628\u0647 \u0634\u0645\u0627 \u0631\u0648\u06CC \u0634\u0628\u06A9\u0647 \u062F\u0631 \u062C\u0631\u06CC\u0627\u0646 \u0627\u0633\u062A\u061B \u0686\u0646\u062F \u062F\u0642\u06CC\u0642\u0647 \u0637\u0648\u0644 \u0645\u06CC\u200C\u06A9\u0634\u062F \u0648 \u067E\u0627\u06CC\u0627\u0646\u0634 \u0647\u0645\u06CC\u0646\u200C\u062C\u0627 \u062E\u0628\u0631 \u062F\u0627\u062F\u0647 \u0645\u06CC\u200C\u0634\u0648\u062F. \u0644\u0627\u0632\u0645 \u0646\u06CC\u0633\u062A \u0635\u0641\u062D\u0647 \u0628\u0627\u0632 \u0628\u0645\u0627\u0646\u062F.", "The winner is chosen. The work's transfer to them and your payment are running on chain \u2014 it takes a few minutes and you'll be told here. The page needn't stay open.") : who === "winner" ? t("\u0634\u0645\u0627 \u0628\u0631\u0646\u062F\u0647\u200C\u06CC \u0627\u06CC\u0646 \u0645\u0632\u0627\u06CC\u062F\u0647\u200C\u0627\u06CC\u062F. \u0627\u062B\u0631 \u062F\u0631 \u062D\u0627\u0644 \u0627\u0646\u062A\u0642\u0627\u0644 \u0628\u0647 \u06A9\u06CC\u0641 \u067E\u0648\u0644 \u0634\u0645\u0627\u0633\u062A\u061B \u0686\u0646\u062F \u062F\u0642\u06CC\u0642\u0647 \u0637\u0648\u0644 \u0645\u06CC\u200C\u06A9\u0634\u062F \u0648 \u067E\u0627\u06CC\u0627\u0646\u0634 \u0647\u0645\u06CC\u0646\u200C\u062C\u0627 \u062E\u0628\u0631 \u062F\u0627\u062F\u0647 \u0645\u06CC\u200C\u0634\u0648\u062F.", "You won this auction. The work is on its way to your wallet \u2014 it takes a few minutes and you'll be told here.") : t("\u0628\u0631\u0646\u062F\u0647 \u0627\u0646\u062A\u062E\u0627\u0628 \u0634\u062F\u0647 \u0648 \u0627\u0646\u062A\u0642\u0627\u0644\u200C\u0647\u0627 \u0631\u0648\u06CC \u0634\u0628\u06A9\u0647 \u062F\u0631 \u062C\u0631\u06CC\u0627\u0646 \u0627\u0633\u062A. \u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u062A\u0627\u0632\u0647 \u067E\u0630\u06CC\u0631\u0641\u062A\u0647 \u0646\u0645\u06CC\u200C\u0634\u0648\u062F.", "A winner is chosen and the transfers are running on chain. No new bids are taken.");
      $[9] = t;
      $[10] = who;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] !== t7) {
      t8 = <p>{t7}</p>;
      $[12] = t7;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    let t9;
    if ($[14] !== t6 || $[15] !== t8) {
      t9 = <div className="ap-settle" data-state="settling" role="status">{t4}{t6}{t8}</div>;
      $[14] = t6;
      $[15] = t8;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    return t9;
  }
  if (s.state === "settled") {
    let t4;
    if ($[17] !== explorer || $[18] !== s.nftHash || $[19] !== t) {
      t4 = s.nftHash ? {
        href: `${explorer}${s.nftHash}`,
        label: t("\u062A\u0631\u0627\u06A9\u0646\u0634 \u0627\u0646\u062A\u0642\u0627\u0644 \u0627\u062B\u0631", "The work's transfer")
      } : null;
      $[17] = explorer;
      $[18] = s.nftHash;
      $[19] = t;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    let t5;
    if ($[21] !== explorer || $[22] !== s.paymentHash || $[23] !== t) {
      t5 = s.paymentHash ? {
        href: `${explorer}${s.paymentHash}`,
        label: t("\u062A\u0631\u0627\u06A9\u0646\u0634 \u067E\u0631\u062F\u0627\u062E\u062A", "The payment")
      } : null;
      $[21] = explorer;
      $[22] = s.paymentHash;
      $[23] = t;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    let t6;
    if ($[25] !== t4 || $[26] !== t5) {
      t6 = [t4, t5].filter(Boolean);
      $[25] = t4;
      $[26] = t5;
      $[27] = t6;
    } else {
      t6 = $[27];
    }
    const links = t6 as {
      href: string;
      label: string;
    }[];
    let t7;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <CircleCheck aria-hidden={true} className="ap-settle-ico" />;
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    let t8;
    if ($[29] !== t) {
      t8 = t("\u062A\u0633\u0648\u06CC\u0647 \u0627\u0646\u062C\u0627\u0645 \u0634\u062F", "Settled");
      $[29] = t;
      $[30] = t8;
    } else {
      t8 = $[30];
    }
    let t9;
    if ($[31] !== t8) {
      t9 = <b>{t8}</b>;
      $[31] = t8;
      $[32] = t9;
    } else {
      t9 = $[32];
    }
    let t10;
    if ($[33] !== t || $[34] !== who) {
      t10 = who === "owner" ? t("\u0627\u062B\u0631 \u0628\u0647 \u0628\u0631\u0646\u062F\u0647 \u0631\u0633\u06CC\u062F \u0648 \u0645\u0628\u0644\u063A \u0628\u0647 \u06A9\u06CC\u0641 \u067E\u0648\u0644 \u0634\u0645\u0627 \u0648\u0627\u0631\u06CC\u0632 \u0634\u062F.", "The work reached the winner and the payment reached your wallet.") : who === "winner" ? t("\u0627\u062B\u0631 \u0628\u0647 \u06A9\u06CC\u0641 \u067E\u0648\u0644 \u0634\u0645\u0627 \u0645\u0646\u062A\u0642\u0644 \u0634\u062F.", "The work is in your wallet.") : t("\u0627\u06CC\u0646 \u0645\u0632\u0627\u06CC\u062F\u0647 \u062A\u0633\u0648\u06CC\u0647 \u0634\u062F.", "This auction has settled.");
      $[33] = t;
      $[34] = who;
      $[35] = t10;
    } else {
      t10 = $[35];
    }
    let t11;
    if ($[36] !== t10) {
      t11 = <p>{t10}</p>;
      $[36] = t10;
      $[37] = t11;
    } else {
      t11 = $[37];
    }
    let t12;
    if ($[38] !== links) {
      t12 = links.length ? <span className="ap-settle-links">{links.map(_temp)}</span> : null;
      $[38] = links;
      $[39] = t12;
    } else {
      t12 = $[39];
    }
    let t13;
    if ($[40] !== t11 || $[41] !== t12 || $[42] !== t9) {
      t13 = <div className="ap-settle" data-state="settled" role="status">{t7}{t9}{t11}{t12}</div>;
      $[40] = t11;
      $[41] = t12;
      $[42] = t9;
      $[43] = t13;
    } else {
      t13 = $[43];
    }
    return t13;
  }
  const stuck = s.state === "stuck";
  let t4;
  if ($[44] !== stuck) {
    t4 = stuck ? <OctagonAlert aria-hidden={true} className="ap-settle-ico" /> : <CircleAlert aria-hidden={true} className="ap-settle-ico" />;
    $[44] = stuck;
    $[45] = t4;
  } else {
    t4 = $[45];
  }
  let t5;
  if ($[46] !== stuck || $[47] !== t) {
    t5 = stuck ? t("\u067E\u0631\u062F\u0627\u062E\u062A \u062F\u0631 \u062C\u0631\u06CC\u0627\u0646 \u0645\u0627\u0646\u062F\u0647", "The payment is stuck") : t("\u062A\u0633\u0648\u06CC\u0647 \u0627\u0646\u062C\u0627\u0645 \u0646\u0634\u062F", "The settlement didn't go through");
    $[46] = stuck;
    $[47] = t;
    $[48] = t5;
  } else {
    t5 = $[48];
  }
  let t6;
  if ($[49] !== t5) {
    t6 = <b>{t5}</b>;
    $[49] = t5;
    $[50] = t6;
  } else {
    t6 = $[50];
  }
  let t7;
  if ($[51] !== s.message) {
    t7 = s.message ? <p className="ap-settle-why">{s.message}</p> : null;
    $[51] = s.message;
    $[52] = t7;
  } else {
    t7 = $[52];
  }
  let t8;
  if ($[53] !== stuck || $[54] !== t || $[55] !== who) {
    t8 = stuck ? who === "owner" ? t("\u062F\u06A9\u0645\u0647\u200C\u06CC \u0627\u0646\u062A\u062E\u0627\u0628 \u0628\u0631\u0646\u062F\u0647 \u0631\u0627 \u062F\u0648\u0628\u0627\u0631\u0647 \u0646\u0632\u0646\u06CC\u062F. \u062A\u0627 \u0631\u0648\u0634\u0646 \u0634\u062F\u0646 \u0648\u0636\u0639\u06CC\u062A \u0635\u0628\u0631 \u06A9\u0646\u06CC\u062F \u06CC\u0627 \u0628\u0627 \u067E\u0634\u062A\u06CC\u0628\u0627\u0646\u06CC \u062A\u0645\u0627\u0633 \u0628\u06AF\u06CC\u0631\u06CC\u062F\u061B \u0648\u0636\u0639\u06CC\u062A \u0647\u0645\u06CC\u0646\u200C\u062C\u0627 \u0628\u0647\u200C\u0631\u0648\u0632 \u0645\u06CC\u200C\u0634\u0648\u062F.", "Don't choose the winner again. Wait for it to clear or contact support \u2014 this updates here by itself.") : t("\u062A\u0633\u0648\u06CC\u0647 \u0628\u0647 \u062A\u0623\u062E\u06CC\u0631 \u0627\u0641\u062A\u0627\u062F\u0647\u061B \u0648\u0636\u0639\u06CC\u062A \u0647\u0645\u06CC\u0646\u200C\u062C\u0627 \u0628\u0647\u200C\u0631\u0648\u0632 \u0645\u06CC\u200C\u0634\u0648\u062F.", "The settlement is delayed \u2014 this updates here by itself.") : who === "owner" ? t("\u0645\u0632\u0627\u06CC\u062F\u0647 \u062F\u0648\u0628\u0627\u0631\u0647 \u0641\u0639\u0627\u0644 \u0627\u0633\u062A\u061B \u0645\u06CC\u200C\u062A\u0648\u0627\u0646\u06CC\u062F \u0647\u0645\u06CC\u0646 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u0631\u0627 \u062F\u0648\u0628\u0627\u0631\u0647 \u0628\u0631\u0646\u062F\u0647 \u06A9\u0646\u06CC\u062F.", "The auction is active again \u2014 you can choose the same bid again.") : t("\u0645\u0632\u0627\u06CC\u062F\u0647 \u062F\u0648\u0628\u0627\u0631\u0647 \u0641\u0639\u0627\u0644 \u0627\u0633\u062A.", "The auction is active again.");
    $[53] = stuck;
    $[54] = t;
    $[55] = who;
    $[56] = t8;
  } else {
    t8 = $[56];
  }
  let t9;
  if ($[57] !== t8) {
    t9 = <p>{t8}</p>;
    $[57] = t8;
    $[58] = t9;
  } else {
    t9 = $[58];
  }
  let t10;
  if ($[59] !== s.state || $[60] !== t4 || $[61] !== t6 || $[62] !== t7 || $[63] !== t9) {
    t10 = <div className="ap-settle" data-state={s.state} role="alert">{t4}{t6}{t7}{t9}</div>;
    $[59] = s.state;
    $[60] = t4;
    $[61] = t6;
    $[62] = t7;
    $[63] = t9;
    $[64] = t10;
  } else {
    t10 = $[64];
  }
  return t10;
}
function _temp(l) {
  return <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" className="v2-focus">{l.label}<ExternalLink aria-hidden={true} /></a>;
}